/**
 * Display + guards for Vesta loan purpose: Purchase / Refinance only,
 * with refinance cash-out vs rate/term via refinanceCashOutDeterminationType.
 */

export const REFINANCE_CASH_OUT_DETERMINATION_OPTIONS = [
  { value: 'CashOut', label: 'Cash-out' },
  { value: 'NoOrLimitedCashOut', label: 'No / limited cash-out (rate/term)' },
];

/**
 * @param {Record<string, unknown> | null | undefined} loan
 * @returns {string | null}
 */
export function getRefinanceCashOutDeterminationType(loan) {
  if (!loan) return null;
  const raw =
    loan.refinanceCashOutDeterminationType ??
    loan.RefinanceCashOutDeterminationType ??
    null;
  return raw == null || raw === '' ? null : String(raw);
}

/**
 * @param {Record<string, unknown> | null | undefined} loan
 */
export function isPurchaseLoan(loan) {
  return (loan?.loanPurpose || '') === 'Purchase';
}

/**
 * Refinance includes legacy CashOutRefinance single-field encoding.
 * @param {Record<string, unknown> | null | undefined} loan
 */
export function isRefinanceLoan(loan) {
  const p = loan?.loanPurpose || '';
  return p === 'Refinance' || p === 'CashOutRefinance';
}

/**
 * Cash-out refi per API: Refinance + CashOut determination (not a separate loan purpose).
 * Legacy: loanPurpose === 'CashOutRefinance'.
 * @param {Record<string, unknown> | null | undefined} loan
 */
export function isCashOutRefinanceFromApi(loan) {
  if (!loan) return false;
  if (loan.loanPurpose === 'CashOutRefinance') return true;
  if (loan.loanPurpose !== 'Refinance') return false;
  return getRefinanceCashOutDeterminationType(loan) === 'CashOut';
}

/**
 * Single-line label for summaries and read-only loan purpose rows.
 * @param {Record<string, unknown> | null | undefined} loan
 */
export function formatLoanPurposeDisplay(loan) {
  if (!loan) return '—';
  const purpose = loan.loanPurpose || '';
  const det = getRefinanceCashOutDeterminationType(loan);

  if (purpose === 'CashOutRefinance') return 'Cash-out refinance';

  if (purpose === 'Refinance') {
    if (det === 'CashOut') return 'Cash-out refinance';
    if (det === 'NoOrLimitedCashOut') return 'No / limited cash-out refinance (rate/term)';
    return 'Refinance';
  }

  if (purpose === 'Purchase') return 'Purchase';

  return purpose || '—';
}

/**
 * @param {Record<string, unknown> | null | undefined} loan
 * @returns {number | null}
 */
export function getCashOutAmount(loan) {
  if (!loan) return null;
  const raw = loan.cashOutAmount ?? loan.CashOutAmount;
  if (raw == null || raw === '') return null;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) ? n : null;
}
